import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  IMAGE_ASSET,
  SERVICE_CANCELLATION_SEARCH_CREATION_CONTENT,
  SERVICE_CANCELLED_RESPONSE,
} from 'consts'
import {useTranslation} from 'i18n'
import {GiftShopRootEmpty} from 'pages'
import {requestData} from 'services'
import {GiftShopGetExploreCreatorResponse} from 'types'
import {useDebounce, useHistory} from 'utils'
import {useWindowLayout, useWindowMode} from 'windows'
import {Icon, Input, ListLazy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopSearchByCreatorContentItem} from '../ContentItem'

const StyledSearchContainer = styled.div`
  width: 100%;
  margin-bottom: ${convertUnit(8)};
`

export default function GiftShopSearchByCreatorContent() {
  const {translate} = useTranslation()
  const [search, setSearch] = useState('')
  const mode = useWindowMode()
  const {width} = useWindowLayout()
  const emptyImg = IMAGE_ASSET('giftshop', 'not-found.png')
  const debounce = useDebounce()
  const history = useHistory()

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      const response = await requestData('giftshop_get_explore_creator', {
        cancelId: SERVICE_CANCELLATION_SEARCH_CREATION_CONTENT,
        params: {
          page,
          limit,
          q: search,
        },
      })

      if (
        typeof response === 'string' &&
        response === SERVICE_CANCELLED_RESPONSE
      ) {
        return undefined
      }

      return typeof response !== 'string' && response.status === 200
        ? response.data.result
        : []
    },
    [search],
  )

  const handleRenderSearch = useMemo(
    () => (
      <StyledSearchContainer>
        <Input
          placeholder={translate('giftShop:searchByCreatorPlaceholder')}
          placeholderColor="gray_3"
          leftIcon={<Icon type="search" color="gray_3" size={16} />}
          containerStyle={{
            margin: `${convertUnit(12)} ${convertUnit(20)}`,
          }}
          defaultValue={search}
          onChangeText={(text) => debounce(() => setSearch(text))}
          rightIcon="delete"
          onRightIconClick={() => setSearch('')}
        />
      </StyledSearchContainer>
    ),
    [debounce, search, translate],
  )

  const handleRenderItem = useCallback(
    (item: GiftShopGetExploreCreatorResponse) => (
      <GiftShopSearchByCreatorContentItem
        data={item}
        onClickItem={() =>
          history.pushQuery({
            path: 'giftshop_search_by_creator_creation_content',
            state: {
              photo: item.profile_url,
              bio: item.biodata,
            },
            queryObject: {
              creator_id: item.creator_id,
              username: item.username,
            },
          })
        }
      />
    ),
    [history],
  )

  const handleRenderEmpty = useMemo(
    () => (
      <GiftShopRootEmpty
        image={emptyImg}
        imageAlt={emptyImg}
        bottomElement={
          <>
            <Paragraph fontSize="l" fontWeight="bold">
              {translate('giftShop:searchByCreatorEmptyTitle')}
            </Paragraph>
            <Paragraph color="gray_5">
              {translate('giftShop:searchByCreatorEmptyInfo')}
            </Paragraph>
          </>
        }
      />
    ),
    [emptyImg, translate],
  )

  return (
    <ListLazy
      loadData={handleLoadData}
      keyExtractor={(item) => item.creator_id}
      renderItem={handleRenderItem}
      search={search}
      listHeaderElement={handleRenderSearch}
      listEmptyElement={handleRenderEmpty}
      numColumns={mode === 'website' ? Math.floor(width / 450) : 1}
    />
  )
}
